import { useEffect, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, API_BASE } from "../lib/api";

const KEY = ["notifications"];

/**
 * useNotifications — list + unread count with live SSE push.
 * Used in the authed Topbar (cookie-auth EventSource; browser auto-reconnects).
 */
export function useNotifications() {
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: KEY,
    queryFn: async () => (await api.get("/notifications")).data,
    staleTime: 30_000,
  });

  useEffect(() => {
    const es = new EventSource(`${API_BASE}/notifications/stream`, { withCredentials: true });
    es.onmessage = (e) => {
      let n;
      try {
        n = JSON.parse(e.data);
      } catch {
        return;
      }
      if (!n || !n.id) return;
      qc.setQueryData(KEY, (prev) => {
        const items = prev?.items || [];
        if (items.some((x) => x.id === n.id)) return prev;
        return { items: [n, ...items].slice(0, 50), unread_count: (prev?.unread_count || 0) + 1 };
      });
    };
    es.onerror = () => {}; // EventSource reconnects automatically
    return () => es.close();
  }, [qc]);

  const markAllRead = useCallback(async () => {
    qc.setQueryData(KEY, (prev) =>
      prev
        ? { ...prev, items: prev.items.map((i) => ({ ...i, read: true })), unread_count: 0 }
        : prev
    );
    try {
      await api.post("/notifications/read", {});
    } catch {
      qc.invalidateQueries({ queryKey: KEY });
    }
  }, [qc]);

  return { items: data?.items || [], unread: data?.unread_count || 0, markAllRead };
}
